import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { CheckOutlined, CopyOutlined } from '@ant-design/icons';
import {
  DOG_EXPRESSIONS,
  HUMAN_EXPRESSIONS,
  INTENSITY_ORDER,
} from '../illustration/expressionCatalog';
import {
  ANIMATION_CATALOG_VERSION,
  ANIMATION_SOURCES,
  animationCopyLine,
  bodyFor,
  effortWords,
} from '../illustration/animationCatalog';
import EngineSources from './EngineSources';
import './ExpressionEnginePage.css';

const COLUMNS = [
  { intensity: 'small', scale: 'Small', band: 'Mild' },
  { intensity: 'core', scale: 'Core', band: 'The emotion' },
  { intensity: 'big', scale: 'Big', band: 'Intense' },
  { intensity: 'out-of-control', scale: 'Out of control', band: 'Extreme' },
  { intensity: 'de-escalating', scale: 'De-escalating', band: 'Resolve' },
];

const TINT = {
  small: 6,
  core: 11,
  big: 17,
  'out-of-control': 25,
};

const FAMILY = {
  happiness: 'happiness',
  sadness: 'sadness',
  anger: 'anger',
  fear: 'fear',
  surprise: 'surprise',
  disgust: 'disgust',
  interest: 'interest',
  care: 'care',
  rest: 'rest',
  anticipation: 'anticipation',
  longing: 'longing',
  joy: 'happiness',
  frustration: 'anger',
  alert: 'interest',
  hopeful: 'anticipation',
  subdued: 'longing',
};

function titleCase(value) {
  const text = String(value || '');
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

function sentence(value) {
  const text = String(value || '').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

function rowsOf(entries) {
  const order = [];
  const grouped = new Map();
  entries.forEach((entry) => {
    if (!grouped.has(entry.emotion)) {
      grouped.set(entry.emotion, []);
      order.push(entry.emotion);
    }
    grouped.get(entry.emotion).push(entry);
  });
  return order.map((emotion) => {
    const cells = grouped.get(emotion);
    return {
      emotion,
      family: FAMILY[emotion] || 'rest',
      kind: cells[0]?.kind === 'dog' ? 'dog' : 'person',
      cells: INTENSITY_ORDER.map(
        (intensity) => cells.find((entry) => entry.intensity === intensity) || null
      ),
    };
  });
}

function emptyLabel(emotion, intensity, hasFace) {
  if (!hasFace) {
    if (emotion === 'frustration' && intensity === 'out-of-control') {
      return 'N/A — kept off-model for a children’s book';
    }
    return 'N/A';
  }
  return 'Not written yet';
}

function CopyBodyButton({ text, name }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <button
      type="button"
      className="expression-engine__copy"
      aria-label={copied ? `Copied ${name}` : `Copy ${name}`}
      onClick={async (event) => {
        event.stopPropagation();
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          message.success('Copied');
          clearTimeout(timer.current);
          timer.current = setTimeout(() => setCopied(false), 1600);
        } catch {
          message.error('Could not copy');
        }
      }}
    >
      {copied ? <CheckOutlined /> : <CopyOutlined />}
    </button>
  );
}

function AnimationMatrix({ title, intro, rows, onShow, onHide }) {
  return (
    <section className="expression-engine__section">
      <h2>{title}</h2>
      <p className="expression-engine__sub">{intro}</p>
      <div className="expression-engine__scroll" onScroll={onHide}>
        <div className="expression-engine__matrix" role="grid" aria-label={title}>
          <div className="expression-engine__corner">Emotion / Intensity</div>
          {COLUMNS.map((column) => (
            <div
              key={column.intensity}
              className={
                column.intensity === 'de-escalating'
                  ? 'expression-engine__col expression-engine__col--resolve'
                  : 'expression-engine__col'
              }
              role="columnheader"
            >
              <span className="expression-engine__scale">{column.scale}</span>
              <span className="expression-engine__band">{column.band}</span>
            </div>
          ))}
          {rows.map((row) => (
            <React.Fragment key={row.emotion}>
              <div
                className="expression-engine__row-head"
                style={{ '--fam': `var(--color-expression-${row.family})`, '--fam-wash': `var(--color-expression-${row.family}-wash)` }}
                role="rowheader"
              >
                <span className="expression-engine__name">
                  <span className="expression-engine__dot" aria-hidden="true" />
                  {titleCase(row.emotion)}
                </span>
                <span className="expression-engine__aus">Body</span>
              </div>
              {row.cells.map((cell, index) => {
                const intensity = INTENSITY_ORDER[index];
                const body = cell ? bodyFor(cell.id, cell.kind || row.kind) : null;
                if (!body) {
                  return (
                    <div key={intensity} className="expression-engine__cell expression-engine__cell--empty" role="gridcell">
                      <span>{emptyLabel(row.emotion, intensity, Boolean(cell))}</span>
                    </div>
                  );
                }
                const effort = effortWords(body.effort);
                const line = sentence(body.fragment);
                const resolve = intensity === 'de-escalating';
                const copyText = animationCopyLine(cell, body);
                return (
                  <div
                    key={cell.id}
                    className={
                      resolve
                        ? 'expression-engine__cell expression-engine__cell--resolve'
                        : 'expression-engine__cell'
                    }
                    style={{
                      '--fam': `var(--color-expression-${row.family})`,
                      '--tint': TINT[intensity] || 6,
                    }}
                    role="gridcell"
                    onMouseEnter={(event) => onShow(event.currentTarget, cell, body)}
                    onMouseLeave={onHide}
                  >
                    <span className="expression-engine__label">
                      <span className="expression-engine__nm">{cell.label}</span>
                      <span className="expression-engine__lvl">{effort}</span>
                    </span>
                    <span className="expression-engine__frag">{line}</span>
                    <CopyBodyButton text={copyText} name={`${titleCase(row.emotion)} ${cell.label}`} />
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function AnimationEnginePage() {
  const humanRows = useMemo(() => rowsOf(HUMAN_EXPRESSIONS), []);
  const dogRows = useMemo(() => rowsOf(DOG_EXPRESSIONS), []);
  const [tip, setTip] = useState(null);
  const tipRef = useRef(null);

  useEffect(() => {
    document.title = 'Animation engine · Illustration Studio';
  }, []);

  useEffect(() => {
    if (!tip) return undefined;
    const hide = () => setTip(null);
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [tip]);

  useLayoutEffect(() => {
    if (!tip || !tipRef.current) return;
    const node = tipRef.current;
    const rect = tip.rect;
    const width = node.offsetWidth;
    const height = node.offsetHeight;
    const left = Math.max(8, Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - 8));
    let top = rect.top - height - 10;
    if (top < 8) top = Math.min(rect.bottom + 10, window.innerHeight - height - 8);
    node.style.left = `${left}px`;
    node.style.top = `${top}px`;
  }, [tip]);

  const showTip = (node, cell, body) => {
    setTip({
      name: cell.label,
      face: sentence(cell.fragment),
      bap: body.bap,
      basis: body.basis,
      rect: node.getBoundingClientRect(),
    });
  };

  return (
    <div className="workflow-shell__content expression-engine-page">
      <p className="expression-engine__eyebrow">Draft · body · Catalog {ANIMATION_CATALOG_VERSION}</p>
      <h1 className="expression-engine__title">The Animation Engine</h1>
      <p className="expression-engine__lede">
        Every cell with a face has a body line. Hover for the finding. A band with no face stays empty.
      </p>

      <AnimationMatrix
        title="Human matrix"
        intro="Eleven rows, five bands. Every cell with a face has a body line."
        rows={humanRows}
        onShow={showTip}
        onHide={() => setTip(null)}
      />
      <AnimationMatrix
        title="Dog matrix"
        intro="A cell is filled only when a published dog posture describes it. A band with no face stays N/A."
        rows={dogRows}
        onShow={showTip}
        onHide={() => setTip(null)}
      />

      <EngineSources
        id="animation-sources-title"
        intro="These sources are the ones the filled cells use. Hover a cell for the finding."
        sources={ANIMATION_SOURCES}
      />

      <p className="expression-engine__footer">
        Animation engine · catalog {ANIMATION_CATALOG_VERSION} · 2026-10-31
      </p>

      {tip ? (
        <div className="expression-engine__tip" ref={tipRef} role="tooltip">
          <h3>{tip.name}</h3>
          <ul>
            <li><span>Finding</span><span>{tip.basis}</span></li>
            <li><span>Parts</span><span>{tip.bap}</span></li>
            <li><span>Face</span><span>{tip.face}</span></li>
          </ul>
          <p>The four effort words are our reading of this movement, not a Laban result.</p>
        </div>
      ) : null}
    </div>
  );
}
